import { EditableElement } from './types';

/**
 * Bring elements to front (highest z-index)
 * Moves selected elements to the end of the array
 */
export const bringToFront = (
  elements: EditableElement[],
  selectedIds: string[]
): EditableElement[] => {
  const selected = elements.filter((el) => selectedIds.includes(el.id));
  const others = elements.filter((el) => !selectedIds.includes(el.id));
  
  return [...others, ...selected];
};

/**
 * Send elements to back (lowest z-index)
 * Moves selected elements to the beginning of the array
 */
export const sendToBack = (
  elements: EditableElement[],
  selectedIds: string[]
): EditableElement[] => {
  const selected = elements.filter((el) => selectedIds.includes(el.id));
  const others = elements.filter((el) => !selectedIds.includes(el.id));
  
  return [...selected, ...others];
};

/**
 * Bring elements forward by one level
 * Moves each selected element one position toward the end
 */
export const bringForward = (
  elements: EditableElement[],
  selectedIds: string[]
): EditableElement[] => {
  const result = [...elements];
  
  // Process from end to start to avoid conflicts
  for (let i = result.length - 2; i >= 0; i--) {
    if (selectedIds.includes(result[i].id) && !selectedIds.includes(result[i + 1].id)) {
      // Swap with next element
      [result[i], result[i + 1]] = [result[i + 1], result[i]];
    }
  }
  
  return result;
};

/**
 * Send elements backward by one level
 * Moves each selected element one position toward the beginning
 */
export const sendBackward = (
  elements: EditableElement[],
  selectedIds: string[]
): EditableElement[] => {
  const result = [...elements];
  
  // Process from start to end to avoid conflicts
  for (let i = 1; i < result.length; i++) {
    if (selectedIds.includes(result[i].id) && !selectedIds.includes(result[i - 1].id)) {
      // Swap with previous element
      [result[i], result[i - 1]] = [result[i - 1], result[i]];
    }
  }
  
  return result;
};

/**
 * Get the z-index (order) of an element
 */
export const getElementZIndex = (
  elements: EditableElement[],
  elementId: string
): number => {
  return elements.findIndex((el) => el.id === elementId);
};

/**
 * Check if element can move forward
 */
export const canMoveForward = (
  elements: EditableElement[],
  elementId: string
): boolean => {
  const index = getElementZIndex(elements, elementId);
  return index !== -1 && index < elements.length - 1;
};

/**
 * Check if element can move backward
 */
export const canMoveBackward = (
  elements: EditableElement[],
  elementId: string
): boolean => {
  const index = getElementZIndex(elements, elementId);
  return index > 0;
};

/**
 * Sort elements by their order (useful after reordering)
 */
export const sortByOrder = (
  elements: EditableElement[],
  order: string[]
): EditableElement[] => {
  const orderMap = new Map(order.map((id, index) => [id, index]));
  
  return [...elements].sort((a, b) => {
    const orderA = orderMap.get(a.id) ?? Infinity;
    const orderB = orderMap.get(b.id) ?? Infinity;
    return orderA - orderB;
  });
};

/**
 * Get all element IDs in order (useful for saving state)
 */
export const getElementOrder = (elements: EditableElement[]): string[] => {
  return elements.map((el) => el.id);
};
